import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { AppointmentService } from '@/integrations/supabase/services';
import { useNavigate } from 'react-router-dom';
import { Calendar, Clock, MapPin } from 'lucide-react';
import { DashboardLayout } from '@/components/DashboardLayout';

const Appointment = () => {
  const [formData, setFormData] = useState({
    officeLocation: '',
    appointmentDate: '',
    appointmentTime: '',
    notes: ''
  });
  const [appointments, setAppointments] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();

  const voivodeshipOffices = [
    { value: 'warsaw', label: 'Warsaw - Mazowieckie Voivodeship Office' },
    { value: 'krakow', label: 'Kraków - Małopolskie Voivodeship Office' },
    { value: 'gdansk', label: 'Gdańsk - Pomorskie Voivodeship Office' },
    { value: 'wroclaw', label: 'Wrocław - Dolnośląskie Voivodeship Office' },
    { value: 'poznan', label: 'Poznań - Wielkopolskie Voivodeship Office' },
    { value: 'lodz', label: 'Łódź - Łódzkie Voivodeship Office' }
  ];

  const timeSlots = [
    '09:00', '09:30', '10:00', '10:30', '11:00', '11:30',
    '12:00', '12:30', '13:00', '13:30', '14:00', '14:30', '15:00'
  ];

  useEffect(() => {
    if (user) {
      loadAppointments();
    }
  }, [user]);

  const loadAppointments = async () => {
    if (!user) return;
    
    setLoading(true);
    try {
      const data = await AppointmentService.getByUserId(user.uid);
      setAppointments(data || []);
    } catch (error) {
      console.error('Error loading appointments:', error);
      toast({
        title: "Error loading appointments",
        description: "Could not load your appointments.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSelectChange = (name: string, value: string) => {
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;
    
    setSubmitting(true);

    try {
      await AppointmentService.add({
        user_id: user.uid,
        office_location: formData.officeLocation,
        appointment_date: formData.appointmentDate,
        appointment_time: formData.appointmentTime,
        notes: formData.notes,
        status: 'scheduled'
      });

      toast({
        title: "Appointment scheduled",
        description: "Your appointment has been successfully scheduled.",
      });

      setFormData({
        officeLocation: '',
        appointmentDate: '',
        appointmentTime: '',
        notes: ''
      });

      loadAppointments();
      navigate('/dashboard');
    } catch (error) {
      console.error('Error scheduling appointment:', error);
      toast({
        title: "Error scheduling appointment",
        description: "Please try again later.",
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const getMinDate = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split('T')[0];
  };

  if (loading) {
    return (
      <DashboardLayout pageTitle="Schedule Appointment">
        <div className="text-center">Loading...</div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout pageTitle="Schedule Appointment">
      <Card>
        <CardHeader>
          <CardTitle>Schedule Your Appointment</CardTitle>
          <CardDescription>
            Choose your preferred date and time for the Karta Pobytu application submission
          </CardDescription>
        </CardHeader>
        
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="officeLocation">Voivodeship Office</Label>
              <Select 
                value={formData.officeLocation} 
                onValueChange={(value) => handleSelectChange('officeLocation', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select office location" />
                </SelectTrigger>
                <SelectContent>
                  {voivodeshipOffices.map((office) => (
                    <SelectItem key={office.value} value={office.value}>
                      {office.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="appointmentDate">Appointment Date</Label>
              <Input
                id="appointmentDate"
                name="appointmentDate"
                type="date"
                min={getMinDate()}
                value={formData.appointmentDate}
                onChange={handleInputChange}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="appointmentTime">Appointment Time</Label>
              <Select 
                value={formData.appointmentTime} 
                onValueChange={(value) => handleSelectChange('appointmentTime', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select time slot" />
                </SelectTrigger>
                <SelectContent>
                  {timeSlots.map((time) => (
                    <SelectItem key={time} value={time}>
                      {time}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="notes">Additional Notes</Label>
              <Textarea
                id="notes"
                name="notes"
                rows={3}
                value={formData.notes}
                onChange={handleInputChange}
                placeholder="Any special requirements or notes for your appointment"
              />
            </div>

            <div className="flex space-x-4">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => navigate('/dashboard')}
                className="w-full border-sigma-red text-sigma-red hover:bg-sigma-red hover:text-white transition-all duration-200"
              >
                Back to Dashboard
              </Button>
              <Button 
                type="submit" 
                className="w-full bg-sigma-red hover:bg-sigma-dark-red text-white transition-all duration-200" 
                disabled={submitting}
              >
                {submitting ? 'Scheduling...' : 'Schedule Appointment'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </DashboardLayout>
  );
};

export default Appointment;
